import { useMemo, useCallback } from "react";
import { useUFramework } from "../context";
import { PROFILE_DEFAULT_FIELDS } from "../common/constant";

/**
 * useCustomFieldConfig — single-section field-config helpers.
 *
 * Reads `fields[section]` from the framework context. The response under `fields[section]`
 * contains BOTH default-key entries and admin-added customs. When that array is empty
 * (backend hasn't loaded yet, or section has no config), every helper short-circuits to
 * PROFILE_DEFAULT_FIELDS[section]. Once the backend array has any items, it is treated as
 * authoritative and helpers read from it.
 */
const useCustomFieldConfig = (section = '') => {
    const fieldConfigArray = useUFramework(e => e?.common?.fields?.[section]) || [];
    const resolvedDefaults = PROFILE_DEFAULT_FIELDS?.[section] || [];
    const isEmpty = fieldConfigArray.length === 0;

    const defaultsMap = useMemo(() => {
        const map = {};
        resolvedDefaults.forEach(f => { if (f?.defaultKey) map[f.defaultKey] = f; });
        return map;
    }, [resolvedDefaults]);

    const configMap = useMemo(() => {
        const map = {};
        fieldConfigArray.forEach(field => {
            if (field?.defaultKey) map[field.defaultKey] = field;
            if (field?.key) map[field.key] = field;
        });
        return map;
    }, [fieldConfigArray]);

    const getLabel = (defaultKey, defaultLabel) => {
        if (isEmpty) return defaultsMap[defaultKey]?.name || defaultLabel;
        return configMap[defaultKey]?.name || defaultLabel || defaultsMap[defaultKey]?.name;
    };

    const isFieldRequired = (defaultKey, fallbackRequired = false) => {
        if (isEmpty) return defaultsMap[defaultKey]?.isRequired || fallbackRequired;
        return configMap[defaultKey]?.isRequired || fallbackRequired;
    };

    const isFieldVisible = (defaultKey) => {
        if (isEmpty) return defaultsMap[defaultKey]?.isActive || false;
        return configMap[defaultKey]?.isActive || false;
    };

    const isAddEmployeeRequired = (defaultKey, fallbackRequired = false) => {
        if (isEmpty) return defaultsMap[defaultKey]?.isAddEmployeeRequired ?? fallbackRequired;
        return configMap[defaultKey]?.isAddEmployeeRequired ?? fallbackRequired;
    };

    const isAddEmpForm = (defaultKey, fallback = false) => {
        if (isEmpty) return defaultsMap[defaultKey]?.isAddEmpForm ?? fallback;
        return configMap[defaultKey]?.isAddEmpForm ?? fallback;
    };

    const getFieldConfig = (defaultKey) => {
        if (isEmpty) return defaultsMap[defaultKey] || null;
        return configMap[defaultKey] || null;
    };

    const customFields = useMemo(() => {
        if (isEmpty) return [];
        return fieldConfigArray
            .filter(f => !f?.defaultKey && f?.isActive !== false)
            .sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0));
    }, [isEmpty, fieldConfigArray]);

    const allFields = useMemo(() => {
        const source = isEmpty ? resolvedDefaults : fieldConfigArray;
        return source
            .filter(f => f?.isActive !== false)
            .sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0));
    }, [isEmpty, fieldConfigArray, resolvedDefaults]);

    const getCustomFields = () => customFields;
    const getAllFields = () => allFields;

    const prepareCustomSaveData = (memberData, memberId) => {
        const customFields = getCustomFields();
        if (!customFields.length) return null;

        const customData = {};
        if (memberId) customData._id = memberId;

        customFields.forEach(field => {
            const key = field?.key;
            if (key && memberData?.hasOwnProperty(key)) {
                customData[key] = memberData[key] ?? null;
            }
        });

        return Object.keys(customData).length > (memberId ? 1 : 0) ? customData : null;
    };

    return {
        getLabel,
        isFieldRequired,
        isFieldVisible,
        isAddEmployeeRequired,
        isAddEmpForm,
        getFieldConfig,
        getCustomFields,
        getAllFields,
        prepareCustomSaveData,
    };
};

/**
 * useFieldLabel — multi-section label getter.
 *
 * Reads the WHOLE `fields` map from the framework context once, and returns a
 * plain function to resolve labels from ANY section. Use this instead of calling
 * useCustomFieldConfig once per section when you only need labels:
 *
 *   const getLabel = useFieldLabel();
 *   getLabel(PROFILE_SECTION.jobInfo, 'subLocation', 'Sub Location');
 *   getLabel(PROFILE_SECTION.basicInfo, 'gender', 'Gender');
 *
 * The returned getLabel is a normal function — safe inside arrays, JSX,
 * conditionals, and callbacks. Resolution mirrors useCustomFieldConfig's getLabel:
 * backend config (by defaultKey/key) → passed default → PROFILE_DEFAULT_FIELDS.
 */
export const useFieldLabel = () => {
    const fields = useUFramework(e => e?.common?.fields) || {};

    return useCallback((section, key, defaultLabel) => {
        const configArray = fields?.[section] || [];
        const defaults = PROFILE_DEFAULT_FIELDS?.[section] || [];
        const fallback = defaults.find(f => f?.defaultKey === key)?.name;

        if (configArray.length === 0) return fallback || defaultLabel;

        const match = configArray.find(f => f?.defaultKey === key || f?.key === key);
        return match?.name || defaultLabel || fallback;
    }, [fields]);
};

export default useCustomFieldConfig;
